import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import Dashboard from "@/pages/Dashboard";
import CustomersPage from "@/pages/CustomersPage";
import CustomerDetailPage from "@/pages/CustomerDetailPage";
import ImportPage from "@/pages/ImportPage";
import ImportHistoryPage from "@/pages/ImportHistoryPage";
import DuplicatesPage from "@/pages/DuplicatesPage";
import ReportsPage from "@/pages/ReportsPage";
import TeamPage from "@/pages/TeamPage";
import AuditPage from "@/pages/AuditPage";
import SettingsPage from "@/pages/SettingsPage";
import StatementsPage from "@/pages/StatementsPage";
import EmailPage from "@/pages/EmailPage";

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user?.role !== "admin") {
    return (
      <div className="py-24 text-center" data-testid="no-access-screen">
        <h2 className="text-2xl sm:text-3xl font-serif font-semibold">This area is for administrators</h2>
        <p className="text-muted-foreground mt-3">If you need something here, please ask your administrator.</p>
      </div>
    );
  }
  return children;
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground" data-testid="auth-loading">One moment…</p>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function NotFound() {
  return (
    <div className="py-24 text-center" data-testid="not-found-page">
      <h2 className="text-2xl sm:text-3xl font-serif font-semibold">That page doesn't exist</h2>
      <p className="text-muted-foreground mt-3">Use the menu on the left to find what you need.</p>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/" element={<Protected><Layout /></Protected>}>
              <Route index element={<Dashboard />} />
              <Route path="customers" element={<CustomersPage />} />
              <Route path="customers/:id" element={<CustomerDetailPage />} />
              <Route path="import" element={<AdminOnly><ImportPage /></AdminOnly>} />
              <Route path="imports" element={<ImportHistoryPage />} />
              <Route path="duplicates" element={<DuplicatesPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="statements" element={<AdminOnly><StatementsPage /></AdminOnly>} />
              <Route path="email" element={<AdminOnly><EmailPage /></AdminOnly>} />
              <Route path="team" element={<AdminOnly><TeamPage /></AdminOnly>} />
              <Route path="audit" element={<AdminOnly><AuditPage /></AdminOnly>} />
              <Route path="settings" element={<AdminOnly><SettingsPage /></AdminOnly>} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
